import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Box,
  Callout,
  Combobox,
  EmptyState,
  EmptyStateProps,
  Input,
  NoResults,
  RowsSkeletonLoader,
  type ComboboxItem,
} from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { CustomerWalletStatusType, type CustomerWalletStatus } from 'types/customers'

export interface CustomerWalletFilterProps {
  walletNameSearch: string
  isSearching: boolean
  currencyFilter: string
  currencyOptions: ComboboxItem[]
  isFetchingCurrencies: boolean
  statusFilterValues: CustomerWalletStatus[]
  onChangeWalletNameSearch: (query: string) => void
  onCurrencyFilterChange: (value: string) => void
  onStatusFilterChange: (values: CustomerWalletStatus[]) => void
}

export interface CustomerWalletsProps {
  filterProps: CustomerWalletFilterProps
  isCustomerVerified: boolean
  customerName: string
  isLoading: boolean
  walletTableRows: ActionListChildren[]
  AddWalletButton: EmptyStateProps['PrimaryButton']
}

export const CustomerWallets = ({
  filterProps,
  isCustomerVerified,
  customerName,
  isLoading,
  walletTableRows,
  AddWalletButton,
}: CustomerWalletsProps) => {
  const {
    walletNameSearch,
    isSearching,
    onChangeWalletNameSearch,
    currencyFilter,
    onCurrencyFilterChange,
    currencyOptions,
    isFetchingCurrencies,
    statusFilterValues,
    onStatusFilterChange,
  } = filterProps
  const { t } = useTranslation()
  const hasWallets = !!walletTableRows?.length
  const defaultStatusFilter: CustomerWalletStatus[] = [CustomerWalletStatusType.ACTIVE]
  const hasNonDefaultStatusFilter =
    statusFilterValues.length !== defaultStatusFilter.length ||
    defaultStatusFilter.some(status => !statusFilterValues.includes(status))
  const hasUserAppliedFilters = isSearching || !!currencyFilter || hasNonDefaultStatusFilter

  const statusFilterItems = [
    { value: CustomerWalletStatusType.ACTIVE, leftText: t('customers.walletStatusActive') },
    { value: CustomerWalletStatusType.INACTIVE, leftText: t('customers.walletStatusInactive') },
    { value: CustomerWalletStatusType.TERMINATED, leftText: t('customers.walletStatusTerminated') },
  ]

  const statusSortOrder: CustomerWalletStatus[] = [
    CustomerWalletStatusType.ACTIVE,
    CustomerWalletStatusType.INACTIVE,
    CustomerWalletStatusType.TERMINATED,
  ]

  const handleClearFilters = useCallback(() => {
    onChangeWalletNameSearch('')
    onCurrencyFilterChange('')
    onStatusFilterChange(defaultStatusFilter)
  }, [onChangeWalletNameSearch, onCurrencyFilterChange, onStatusFilterChange])

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      const updatedSelection = statusFilterValues.includes(value as CustomerWalletStatus)
        ? statusFilterValues.filter(status => status !== value)
        : [...statusFilterValues, value as CustomerWalletStatus]
      onStatusFilterChange(
        [...updatedSelection].sort(
          (a, b) => statusSortOrder.indexOf(a) - statusSortOrder.indexOf(b)
        )
      )
    },
    [statusFilterValues, onStatusFilterChange, statusSortOrder]
  )

  const CustomerWalletView = useMemo(() => {
    if (isLoading) {
      return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
    }

    if (!hasWallets) {
      return <NoResults onClear={handleClearFilters} />
    }

    return <ActionList id="customer-wallets-action-list" items={walletTableRows} />
  }, [isLoading, hasWallets, walletTableRows, handleClearFilters])

  const showFiltersAndTabs = hasWallets || hasUserAppliedFilters || isLoading || isCustomerVerified

  if (!showFiltersAndTabs) {
    return (
      <EmptyState
        testId="customer-wallets-empty-state"
        lottieName="not-verified"
        title={t('customers.walletRestriction.customerNotVerifiedTitle')}
        description={t('customers.walletRestriction.customerNotVerifiedDescription')}
      />
    )
  }

  return (
    <>
      <Box paddingB={24} width="full" flex isFlexWrap gapX={12} gapY={12}>
        <Box className="lg:flex-auto lg:min-w-[200px]">
          <Input
            value={walletNameSearch}
            testId="customer-wallet-name-search-field"
            placeholder={t('customers.customerWalletNameSearchPlaceholder')}
            searchable
            onChange={event => onChangeWalletNameSearch(event.target.value)}
          />
        </Box>
        <Box className="w-auto" style={{ minWidth: '220px' }}>
          <Combobox
            key={`customer-wallet-currency-${currencyFilter}`}
            id="customer-wallet-currency-filter"
            testId="customer-wallet-currency-filter"
            items={currencyOptions}
            defaultValue={currencyFilter}
            selectPlaceholder={t('customers.currencyFilterPlaceholder')}
            disabled={isLoading || isFetchingCurrencies}
            loading={isFetchingCurrencies}
            loadingInline={isFetchingCurrencies}
            onChange={value => onCurrencyFilterChange(value ?? '')}
          />
        </Box>
        {!!onStatusFilterChange && (
          <Box className="w-auto" style={{ minWidth: '220px' }}>
            <Combobox
              id="customer-wallet-status-filter"
              testId="customer-wallet-status-filter"
              searchPlaceholder={t('customers.filterStatusLabel')}
              disabled={isLoading}
              items={statusFilterItems}
              selectPlaceholder={t('customers.filterStatusLabel')}
              checkedValues={statusFilterValues}
              onCheckedChange={handleStatusFilterChange}
            />
          </Box>
        )}
      </Box>
      {!isCustomerVerified && (
        <Box paddingB={16}>
          <Callout variant="info">{t('customers.inactiveCustomerInfoMessage')}</Callout>
        </Box>
      )}
      {CustomerWalletView}
    </>
  )
}
